import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Zap } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4935773c-183d-4ff8-bc9d-3030c509887e/artifacts/mknxgjj1_identra_logo_3d_clean_hd.jpg";

const RegisterPage = () => {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Form Side */}
      <div className="auth-form-side">
        <div className="max-w-md mx-auto w-full">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="Identra" className="h-12 w-auto" />
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-3">Get started</h1>
            <p className="text-muted-foreground text-lg">
              Create your account and start sharing your digital card
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Full name
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-12 h-12 text-base"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-12 text-base"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-12 text-base"
                  data-testid="register-password-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 6 characters
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-[#16335E] hover:bg-[#0A1A34]"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="loading-spinner w-5 h-5" />
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-muted-foreground">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-[#16335E] font-semibold hover:underline"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Visual Side */}
      <div className="auth-visual-identra">
        <div className="relative z-10 text-center text-white">
          <div className="floating-card mb-8 p-6 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#16335E] to-[#00A78B]" />
              <div>
                <p className="font-semibold text-gray-800">Sarah Miller</p>
                <p className="text-sm text-gray-500">Marketing Lead</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs text-gray-400">COMPANY</p>
                <p className="text-sm text-gray-700 font-medium">TechCorp</p>
              </div>
              <div className="w-16 h-16 bg-gray-100 rounded-lg" />
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-6 h-6" />
            <span className="text-lg font-medium">Instant Sharing</span>
          </div>
          <p className="text-white/80 max-w-sm mx-auto">
            QR codes for quick scans. NFC enabled cards for tap-to-share. Track who views your card.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
